import React, { useEffect, useState, useCallback } from "react";
import Card from "components/card";
import InputField from "components/fields/InputField";
import { toast } from "react-toastify";
import { updateCms, getCms } from "api/services/cmsServices";
import TextField from "components/fields/TextField";

// HowItWorkCard component memoized to prevent unnecessary re-renders
const HowItWorkCard = React.memo(({ workKey, work, errors, onChange, onImageUpload, onSave, id }) => (
  <Card extra={"w-full h-full p-6"}>
    <h3 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">Step {workKey.slice(-1)}</h3>
    <InputField
      label="Title*"
      placeholder="Enter Title"
      id={`${workKey}-title`}
      type="text"
      value={work.title}
      onChangeFun={(e) => onChange(workKey, "title", e.target.value)}
    />
    {errors?.title && <p className="mb-2 ml-2 text-sm text-red-500">{errors?.title}</p>}

    <div className="mt-4">
      <TextField
        label="Description*"
        value={work.description}
        onChangeFun={(e) => onChange(workKey, "description", e.target.value)}
        className="mt-2 w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-brand-500 focus:outline-none"
        rows="4"
        placeholder="Enter description"
      />
    </div>
    {errors?.description && <p className="mb-2 ml-2 text-sm text-red-500">{errors?.description}</p>}

    <div className="mt-4">
      <label className="text-sm font-bold text-navy-700 dark:text-white">Upload Image</label>
      <input
        type="file"
        accept="image/*"
        onChange={(e) => onImageUpload(workKey, e)}
        className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-blue-700 hover:file:bg-blue-100"
      />
      {work.imgPreview && (
        <img
          src={work.imgPreview}
          alt={`Step ${workKey.slice(-1)} Preview`}
          className="mt-4 h-40 rounded-lg border object-contain"
        />
      )}
    </div>

    <div className="flex justify-end mt-6">
      <button
        onClick={() => onSave(workKey, id)}
        className="text-md rounded-md bg-brand-500 px-4 py-1.5 font-semibold text-white shadow transition hover:bg-brand-600"
      >
        Save Step {workKey.slice(-1)}
      </button>
    </div>
  </Card>
));

const HowItWorks = () => {
  const [works, setWorks] = useState({
    work1: { title: "", description: "", img: "", imgPreview: "" },
    work2: { title: "", description: "", img: "", imgPreview: "" },
    work3: { title: "", description: "", img: "", imgPreview: "" },
  });

  const [errors, setErrors] = useState({
    work1: {},
    work2: {},
    work3: {},
  });

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    try {
      const [response1, response2, response3] = await Promise.all([
        getCms("how_it_work_1"),
        getCms("how_it_work_2"),
        getCms("how_it_work_3"),
      ]);

      setWorks({
        work1: {
          title: response1?.data?.data?.title || "",
          description: response1?.data?.data?.description || "",
          img: "",
          imgPreview: response1?.data?.data?.image || "",
        },
        work2: {
          title: response2?.data?.data?.title || "",
          description: response2?.data?.data?.description || "",
          img: "",
          imgPreview: response2?.data?.data?.image || "",
        },
        work3: {
          title: response3?.data?.data?.title || "",
          description: response3?.data?.data?.description || "",
          img: "",
          imgPreview: response3?.data?.data?.image || "",
        },
      });
    } catch (error) {
      toast.error("Failed to fetch how it works data");
    }
  };

  const handleChange = useCallback((workKey, field, value) => {
    setWorks((prev) => ({
      ...prev,
      [workKey]: {
        ...prev[workKey],
        [field]: value,
      },
    }));
    setErrors((prev) => ({
      ...prev,
      [workKey]: {
        ...prev[workKey],
        [field]: "",
      },
    }));
  }, []);

  const handleImageUpload = useCallback((workKey, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleChange(workKey, "img", file);
        handleChange(workKey, "imgPreview", reader.result);
      };
      reader.readAsDataURL(file);
    }
  }, [handleChange]);

  const handleSave = useCallback(async (workKey, id) => {
    const currentWork = works[workKey];
    const newErrors = {};
    let hasErrors = false;

    if (!currentWork.title.trim()) {
      newErrors.title = "Title is required";
      hasErrors = true;
    }
    if (!currentWork.description.trim()) {
      newErrors.description = "Description is required";
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors((prev) => ({
        ...prev,
        [workKey]: newErrors,
      }));
      return;
    }

    try {
      const formData = new FormData();
      formData.append("type", `how_it_work_${id}`);
      formData.append('title', currentWork?.title);
      formData.append('description', currentWork?.description);
      formData.append("image", currentWork?.img)

      const response = await updateCms(formData);
      if (response?.status === 200) {
        toast.success(`Step ${workKey.slice(-1)} updated successfully`);
      } else {
        toast.error(`Failed to update step ${workKey.slice(-1)}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Something went wrong");
    }
  }, [works]);

  return (
    <div className="mt-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <HowItWorkCard
          workKey="work1"
          work={works.work1}
          errors={errors.work1}
          onChange={handleChange}
          onImageUpload={handleImageUpload}
          onSave={handleSave}
          id="1"
        />
        <HowItWorkCard
          workKey="work2"
          work={works.work2}
          errors={errors.work2}
          onChange={handleChange}
          onImageUpload={handleImageUpload}
          onSave={handleSave}
          id="2"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-1">
          <HowItWorkCard
            workKey="work3"
            work={works.work3}
            errors={errors.work3}
            onChange={handleChange}
            onImageUpload={handleImageUpload}
            onSave={handleSave}
            id="3"
          />
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
